import { useEffect, useCallback, useRef } from 'react';
import websocketService from '../services/websocketService';

/**
 * Custom hook for managing WebSocket connections and events
 * @param {Object} options - Hook options
 * @param {boolean} options.autoConnect - Auto connect on mount (default: true)
 * @param {Array} options.dependencies - Dependencies to trigger reconnection
 * @returns {Object} WebSocket utilities
 */
export const useWebSocket = (options = {}) => {
  const {
    autoConnect = true,
    dependencies = [],
  } = options;

  const connectionStatusRef = useRef({ isConnected: false });

  // Initialize WebSocket connection
  const connect = useCallback(() => {
    const initialized = websocketService.initialize({ autoConnect: true });
    if (initialized) {
      connectionStatusRef.current = websocketService.getConnectionStatus();
    }
    return initialized;
  }, []);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    websocketService.disconnect();
    connectionStatusRef.current = { isConnected: false };
  }, []);

  // Get current connection status
  const getConnectionStatus = useCallback(() => {
    return websocketService.getConnectionStatus();
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      if (websocketService.getConnectionStatus().isConnected) {
        disconnect();
      }
    };
  }, [autoConnect, connect, disconnect]);

  // Reconnect when dependencies change
  useEffect(() => {
    if (dependencies.length > 0 && websocketService.getConnectionStatus().isConnected) {
      console.log('🔄 WebSocket reconnecting due to dependency change');
      disconnect();
      setTimeout(() => connect(), 1000); // Small delay before reconnection
    }
  }, dependencies);

  return {
    connect,
    disconnect,
    getConnectionStatus,
    isConnected: connectionStatusRef.current.isConnected,
  };
};

/**
 * Hook for listening to specific WebSocket events
 * @param {string} eventName - Event name to listen to
 * @param {Function} callback - Event callback
 * @param {Array} deps - Dependencies for useCallback
 */
export const useWebSocketEvent = (eventName, callback, deps = []) => {
  const stableCallback = useCallback(callback, deps);

  useEffect(() => {
    websocketService.addEventListener(eventName, stableCallback);

    return () => {
      websocketService.removeEventListener(eventName, stableCallback);
    };
  }, [eventName, stableCallback]);
};

/**
 * Hook for managing real-time notifications
 * @param {Object} options - Hook options
 * @returns {Object} Notification utilities
 */
export const useRealtimeNotifications = (options = {}) => {
  const {
    onNewNotification,
    onUnreadCountUpdate,
    onConnectionChange,
  } = options;

  // Listen for new notifications
  useWebSocketEvent('new_notification', (notification) => {
    console.log('🔔 Received real-time notification:', notification);
    
    // Show browser notification if permission granted
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico',
          tag: notification.id,
          requireInteraction: notification.type === 'ERROR',
        });
      } catch (error) {
        console.warn('Failed to show browser notification:', error);
      }
    }

    if (onNewNotification) {
      onNewNotification(notification);
    }
  }, [onNewNotification]);

  // Listen for unread count updates
  useWebSocketEvent('unread_count_update', (data) => {
    console.log('📊 Unread count updated:', data.count);
    
    if (onUnreadCountUpdate) {
      onUnreadCountUpdate(data.count);
    }
  }, [onUnreadCountUpdate]);

  // Listen for connection status changes
  useWebSocketEvent('websocket_connected', () => {
    console.log('🔌 WebSocket connected');
    if (onConnectionChange) {
      onConnectionChange(true);
    }
  }, [onConnectionChange]);

  useWebSocketEvent('websocket_disconnected', () => {
    console.log('🔌 WebSocket disconnected');
    if (onConnectionChange) {
      onConnectionChange(false);
    }
  }, [onConnectionChange]);

  // Track notification interactions
  const trackNotificationRead = useCallback((notificationId, metadata) => {
    websocketService.trackNotificationRead(notificationId, metadata);
  }, []);

  const trackNotificationClicked = useCallback((notificationId, actionType, metadata) => {
    websocketService.trackNotificationClicked(notificationId, actionType, metadata);
  }, []);

  const trackNotificationDismissed = useCallback((notificationId, metadata) => {
    websocketService.trackNotificationDismissed(notificationId, metadata);
  }, []);

  // Room subscription utilities
  const subscribeToRoom = useCallback((room) => {
    websocketService.subscribeToRoom(room);
  }, []);

  const unsubscribeFromRoom = useCallback((room) => {
    websocketService.unsubscribeFromRoom(room);
  }, []);

  return {
    trackNotificationRead,
    trackNotificationClicked,
    trackNotificationDismissed,
    subscribeToRoom,
    unsubscribeFromRoom,
  };
};

/**
 * Hook for requesting browser notification permissions
 * @returns {Object} Permission utilities
 */
export const useNotificationPermissions = () => {
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'not-supported';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    // Request permission
    try {
      const permission = await Notification.requestPermission();
      console.log('🔔 Notification permission:', permission);
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'error';
    }
  }, []);

  const getPermissionStatus = useCallback(() => {
    if (!('Notification' in window)) {
      return 'not-supported';
    }
    return Notification.permission;
  }, []);

  return {
    requestPermission,
    getPermissionStatus,
    isSupported: 'Notification' in window,
    isGranted: 'Notification' in window && Notification.permission === 'granted',
  };
};